import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, CopyFeedbackIcon, Input, Label } from '@shared/ui';
import { useCopyToClipboard } from '@shared/lib/use-copy-to-clipboard';
import { createApiClient } from '@shared/api/client/api-client';
import { createAuthApi } from '@shared/api/lib/auth-api';
import { mapAuthResponseDtoToEntity } from '@entities/auth';
import type { AuthAction, SecondFactorMethod } from '@entities/auth';
import { qrImageSrc } from '@entities/security';
import { applyAuthResponse } from '@entities/auth';
import { otpDigits, shouldAutoSubmitOtp } from '@entities/auth';
import { useAuth } from '@entities/auth';
import { readApiError } from '@shared/api/errors/read-api-error';
import { getApiErrorMessage } from '@shared/lib/get-api-error-message';
import { env } from '@shared/config/env';

export interface LoginChallengeState {
  token: string;
  action: Extract<AuthAction, 'confirm_otp' | 'register_otp' | 'confirm_device'>;
  methods: SecondFactorMethod[];
  sentTo?: string | null;
  resendAfterSec?: number;
}

interface LoginChallengeProps {
  challenge: LoginChallengeState;
  onBack: () => void;
  onLocked: (lockedUntil?: string) => void;
}

export function LoginChallenge({ challenge, onBack, onLocked }: LoginChallengeProps) {
  const { t } = useTranslation('auth');
  const { login } = useAuth();
  const [step, setStep] = useState(challenge);
  const [method, setMethod] = useState<SecondFactorMethod>(challenge.methods[0] ?? 'totp');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const busy = busyAction !== null;
  const [setup, setSetup] = useState<{ secret?: string; qr?: string | null; sentTo?: string | null } | null>(null);
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [savedCodes, setSavedCodes] = useState(false);
  const { copy, isCopied } = useCopyToClipboard();
  const submittingRef = useRef(false);

  const api = () => createAuthApi(createApiClient({ baseURL: env.API_BASE_URL }));

  const finish = async (token: string, action?: AuthAction, codes?: string[]) => {
    if (codes && codes.length > 0) {
      setBackupCodes(codes);
      setSessionToken(token);
      return;
    }
    const mapped = mapAuthResponseDtoToEntity({ token, action: action ?? 'login_success' });
    await applyAuthResponse(mapped, login);
  };

  const handleError = (err: unknown) => {
    const parsed = readApiError(err);
    if (parsed?.code === 'ENVIRONMENT_CHANGED' || parsed?.code === 'LOGIN_LOCKED') {
      onLocked(parsed.lockedUntil);
      return;
    }
    if (parsed?.status === 401 || (err && typeof err === 'object' && 'status' in err && err.status === 401)) {
      setError(t('security.challengeExpired'));
      return;
    }
    setError(getApiErrorMessage(err, t, 'errors.verificationFailed'));
  };

  const submitOtp = async (raw = code) => {
    const nextCode = method === 'backup' ? raw.trim() : otpDigits(raw);
    if (submittingRef.current || busy) return;
    if (method === 'backup' ? nextCode.length < 4 : nextCode.length !== 6) return;

    submittingRef.current = true;
    setBusyAction('otp');
    setError(null);
    try {
      const response = await api().loginConfirm(
        { secondFactor: { method, code: nextCode } },
        step.token,
      );
      if (response.action === 'confirm_device') {
        setStep({
          token: response.token,
          action: 'confirm_device',
          methods: [],
          sentTo: response.sentTo,
          resendAfterSec: response.resendAfterSec,
        });
        setCode('');
        return;
      }
      await finish(response.token, response.action, response.backupCodes);
    } catch (err: unknown) {
      handleError(err);
    } finally {
      submittingRef.current = false;
      setBusyAction(null);
    }
  };

  const submitCode = (raw: string, submit: (value: string) => Promise<void>) => {
    setCode(raw);
    if (shouldAutoSubmitOtp(method, raw)) {
      void submit(raw);
    }
  };

  const startSetup = async (nextMethod: 'totp' | 'email') => {
    setBusyAction(`setup:${nextMethod}`);
    setError(null);
    try {
      const response = await api().setupLoginTwoFa({ method: nextMethod }, step.token);
      setMethod(nextMethod);
      setSetup(
        response.method === 'totp'
          ? { secret: response.code, qr: qrImageSrc(response.qr) }
          : { sentTo: response.sentTo },
      );
    } catch (err: unknown) {
      handleError(err);
    } finally {
      setBusyAction(null);
    }
  };

  const confirmSetup = async (raw = code) => {
    const nextCode = otpDigits(raw);
    if (!setup || submittingRef.current || busy || nextCode.length !== 6) return;

    submittingRef.current = true;
    setBusyAction('confirm-setup');
    setError(null);
    try {
      const response = await api().confirmLoginTwoFa(
        { method: method === 'email' ? 'email' : 'totp', code: nextCode },
        step.token,
      );
      await finish(response.token, response.action ?? 'login_success', response.backupCodes);
    } catch (err: unknown) {
      handleError(err);
    } finally {
      submittingRef.current = false;
      setBusyAction(null);
    }
  };

  const confirmDevice = async (raw = code) => {
    const nextCode = otpDigits(raw);
    if (submittingRef.current || busy || nextCode.length !== 6) return;

    submittingRef.current = true;
    setBusyAction('device');
    setError(null);
    try {
      const response = await api().confirmDevice({ code: nextCode }, step.token);
      await finish(response.token, response.action ?? 'login_success', response.backupCodes);
    } catch (err: unknown) {
      handleError(err);
    } finally {
      submittingRef.current = false;
      setBusyAction(null);
    }
  };

  if (backupCodes) {
    return (
      <div className="flex w-full flex-col gap-4">
        <h2 className="auth-step-title">{t('security.backupTitle')}</h2>
        <p className="text-sm text-muted-foreground">{t('security.backupHint')}</p>
        <pre className="whitespace-pre-wrap rounded-xl bg-muted p-3 text-sm">{backupCodes.join('\n')}</pre>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={() => void copy(backupCodes.join('\n'), t('security.copy'), 'backup-codes')}
          >
            <CopyFeedbackIcon copied={isCopied('backup-codes')} className="mr-2 h-4 w-4" />
            {t('security.copy')}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              const blob = new Blob([backupCodes.join('\n')], { type: 'text/plain' });
              const url = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = 'paygatex-backup-codes.txt';
              link.click();
              URL.revokeObjectURL(url);
            }}
          >
            {t('security.download')}
          </Button>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <Checkbox checked={savedCodes} onCheckedChange={(value) => setSavedCodes(value === true)} />
          {t('security.backupSaved')}
        </label>
        <Button
          type="button"
          variant="default"
          disabled={!savedCodes || busy}
          onClick={() => void finish(sessionToken ?? step.token, 'login_success')}
        >
          {t('security.continue')}
        </Button>
      </div>
    );
  }

  if (step.action === 'confirm_device') {
    return (
      <div className="flex w-full flex-col gap-4">
        <h2 className="auth-step-title">{t('security.deviceTitle')}</h2>
        <p className="text-sm text-muted-foreground">{t('security.deviceHint', { email: step.sentTo ?? '' })}</p>
        <Input
          value={code}
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          disabled={busy}
          onChange={(event) => submitCode(event.target.value, confirmDevice)}
        />
        {error ? <p className="text-xs text-destructive">{error}</p> : null}
        <Button type="button" variant="default" loading={busyAction === 'device'} disabled={otpDigits(code).length < 6 || (busy && busyAction !== 'device')} onClick={() => void confirmDevice()}>
          {t('security.confirm')}
        </Button>
        <Button
          type="button"
          variant="secondary"
          disabled={busy}
          onClick={() => void api().resendDeviceCode(step.token).catch(handleError)}
        >
          {t('security.resend')}
        </Button>
        <Button type="button" variant="ghost" onClick={onBack}>
          {t('back')}
        </Button>
      </div>
    );
  }

  if (step.action === 'register_otp') {
    const methods = (step.methods.length > 0 ? step.methods : ['totp', 'email']).filter(
      (item): item is 'totp' | 'email' => item === 'totp' || item === 'email',
    );
    return (
      <div className="flex w-full flex-col gap-4">
        <h2 className="auth-step-title">{t('security.setupTitle')}</h2>
        <p className="text-sm text-muted-foreground">{t('security.setupHint')}</p>
        <div className="flex gap-2">
          {methods.map((item) => (
            <Button key={item} type="button" variant="secondary" loading={busyAction === `setup:${item}`} disabled={busy && busyAction !== `setup:${item}`} onClick={() => void startSetup(item)}>
              {t(item === 'totp' ? 'security.methodTotp' : 'security.methodEmail')}
            </Button>
          ))}
        </div>
        {setup?.qr ? <img src={setup.qr} alt="" className="h-40 w-40" /> : null}
        {setup?.secret ? <p className="break-all text-sm text-muted-foreground">{setup.secret}</p> : null}
        {setup?.sentTo ? <p className="text-sm text-muted-foreground">{t('security.sentTo', { email: setup.sentTo })}</p> : null}
        <div className="space-y-2">
          <Label htmlFor="setup-code">{t('security.code')}</Label>
          <Input
            id="setup-code"
            value={code}
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            disabled={busy || !setup}
            onChange={(event) => submitCode(event.target.value, confirmSetup)}
          />
        </div>
        {error ? <p className="text-xs text-destructive">{error}</p> : null}
        <Button type="button" variant="default" loading={busyAction === 'confirm-setup'} disabled={!setup || code.trim().length < 6 || (busy && busyAction !== 'confirm-setup')} onClick={() => void confirmSetup()}>
          {t('security.confirm')}
        </Button>
      </div>
    );
  }

  const methods = step.methods.length > 0 ? step.methods : (['totp', 'backup'] as SecondFactorMethod[]);
  return (
    <div className="flex w-full flex-col gap-4">
      <h2 className="auth-step-title">{t('security.otpTitle')}</h2>
      <div className="flex flex-wrap gap-2">
        {methods.map((item) => (
          <Button key={item} type="button" size="sm" variant={item === method ? 'default' : 'secondary'} onClick={() => setMethod(item)}>
            {t(`security.method.${item}`)}
          </Button>
        ))}
      </div>
      {method === 'email' ? (
        <Button
          type="button"
          variant="secondary"
          disabled={busy}
          onClick={() =>
            void api()
              .sendLoginEmailOtp(step.token)
              .then((response) => setStep((prev) => ({ ...prev, sentTo: response.sentTo })))
              .catch(handleError)
          }
        >
          {t('security.sendEmail')}
        </Button>
      ) : null}
      {step.sentTo ? <p className="text-sm text-muted-foreground">{t('security.sentTo', { email: step.sentTo })}</p> : null}
      <Input
        value={code}
        inputMode={method === 'backup' ? 'text' : 'numeric'}
        autoComplete="one-time-code"
        maxLength={method === 'backup' ? 32 : 6}
        disabled={busy}
        onChange={(event) => submitCode(event.target.value, submitOtp)}
      />
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
      <Button
        type="button"
        variant="default"
        loading={busyAction === 'otp'}
        disabled={(method === 'backup' ? code.trim().length < 4 : otpDigits(code).length < 6) || (busy && busyAction !== 'otp')}
        onClick={() => void submitOtp()}
      >
        {t('security.confirm')}
      </Button>
      <Button type="button" variant="ghost" onClick={onBack}>
        {t('back')}
      </Button>
    </div>
  );
}
